import React, { useEffect, useState } from "react";
import { ProgressBar } from "react-loader-spinner";
import { useLocation } from "react-router-dom";
import Footer from "units/dashboard/components/Footer";
import Header from "units/dashboard/components/Header";
import { SidebarProvider } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "units/user/components/UserSidebar";
import { buyPlan, charge, paymentGatewayList } from "./services/paymentServic";
import { useSelector } from "react-redux";
import { getknowlageBaseInfo } from "units/user/services/knowlageBaseInfo";
import { FiInfo } from "react-icons/fi";
import { toast } from "react-toastify";
import AutoFields from "units/user/subscription/plans/components/AutoFields";
import {
    Wallet,
    CreditCard,
    ShieldCheck,
    AlertCircle,
    Sparkles,
    Receipt,
    CheckCircle2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/* ============================================
   STARFIELD BACKGROUND
============================================ */
const StarField = () => {
    const [stars, setStars] = useState([]);

    useEffect(() => {
        const generatedStars = Array.from({ length: 60 }, (_, i) => ({
            id: i,
            top: Math.random() * 100,
            left: Math.random() * 100,
            size: Math.random() * 3 + 1,
            delay: Math.random() * 5,
            duration: Math.random() * 3 + 2,
            opacity: Math.random() * 0.7 + 0.3,
        }));
        setStars(generatedStars);
    }, []);

    return (
        <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
            <div className="absolute inset-0
                            bg-gradient-to-br from-slate-50 via-white to-teal-50/60
                            dark:!from-teal-950 dark:!via-gray-950 dark:!to-black" />

            <div className="absolute inset-0 opacity-40
                            bg-gradient-to-tr from-transparent via-amber-50/40 to-transparent
                            dark:!from-transparent dark:!via-teal-900/20 dark:!to-black/40" />

            <div className="absolute -top-32 -right-32 w-96 h-96
                            bg-gradient-to-br from-teal-300/40 to-teal-500/20
                            dark:!from-teal-600/30 dark:!to-teal-800/20
                            rounded-full blur-3xl animate-float" />

            <div className="absolute -bottom-32 -left-32 w-96 h-96
                            bg-gradient-to-br from-amber-300/40 to-amber-500/20
                            dark:!from-amber-600/20 dark:!to-amber-800/10
                            rounded-full blur-3xl animate-float"
                style={{ animationDelay: "500ms" }} />

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                            w-[600px] h-[600px]
                            bg-gradient-to-br from-teal-200/30 via-amber-100/30 to-teal-200/30
                            dark:!from-teal-800/20 dark:!via-teal-900/15 dark:!to-teal-950/20
                            rounded-full blur-3xl animate-float"
                style={{ animationDelay: "300ms" }} />

            {stars.map((star) => (
                <div key={star.id}
                    className="absolute rounded-full
                               bg-gradient-to-br from-amber-400 to-teal-400
                               dark:!from-amber-300 dark:!to-teal-300"
                    style={{
                        top: `${star.top}%`,
                        left: `${star.left}%`,
                        width: `${star.size}px`,
                        height: `${star.size}px`,
                        opacity: star.opacity,
                        animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
                        boxShadow: `0 0 ${star.size * 3}px rgba(212, 175, 55, 0.6)`,
                    }}
                />
            ))}

            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.3); }
                }
                .animate-float { animation: float 3s ease-in-out infinite; }
            `}</style>
        </div>
    );
};

const PaymentOptions = () => {
    const { t, i18n } = useTranslation(["user"]);
    const isEnglish = i18n.language === "en";

    const formatNumber = (num) => {
        const n = Number(num || 0);
        return isEnglish ? n.toLocaleString("en-US") : n.toLocaleString("fa-IR");
    };

    const [loading, setLoading] = useState(false);
    const [chargeInfo, setChargeInfo] = useState({});
    const [selectedMethod, setSelectedMethod] = useState("");
    const [walletBalance, setWalletBalance] = useState(0);
    const [walletMessage, setWalletMessage] = useState(0);
    const [selectedGateway, setSelectedGateway] = useState(null);
    const [list, setList] = useState([]);
    const [rules, setRules] = useState(false);
    const [autoFieldValues, setAutoFieldValues] = useState({
        autoRenewal: false,
        useWallet: false,
    });
    const accessToken = useSelector((state) => state.auth.accessToken);

    const handleAutoFieldsChange = (values) => {
        setAutoFieldValues(values);
    };

    useEffect(() => {
        const fetchGateways = async () => {
            try {
                const res = await paymentGatewayList(accessToken);
                if (res?.ok) setList(res?.data);
            } catch (error) { }
        };
        fetchGateways();
    }, []);

    useEffect(() => {
        const getUser = async () => {
            const res = await getknowlageBaseInfo(accessToken);
            if (res?.ok) setWalletBalance(res?.data?.wallet);
        };
        getUser();
    }, []);

    const cleanString = (str) => {
        return str
            .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
            .replace(/[^\d۰-۹٬,]/g, "");
    };

    const convertPersianToEnglish = (str) => {
        const cleaned = cleanString(str);
        return cleaned
            .replace(/[۰-۹]/g, (char) =>
                String.fromCharCode(char.charCodeAt(0) - 0x6f0)
            )
            .replace(/٬|,/g, "");
    };

    const location = useLocation();
    const { finalPrice, offerCode, planId } = location.state || {};
    const rawFinalPrice = location.state?.finalPrice || "۰";
    const finalPriceString = convertPersianToEnglish(String(rawFinalPrice));
    const finalPriceNumber = parseInt(finalPriceString, 10);

    const isWalletDisabled = walletBalance < finalPriceNumber;
    const isGatewayDisabled = !finalPriceNumber || finalPriceNumber <= 0;

    const handleSelect = (method, gateway = "") => {
        if (method === "wallet" && isWalletDisabled) return;
        if (method === "ipg" && isGatewayDisabled) return;
        setSelectedMethod(method);
        if (method === "ipg") setSelectedGateway(gateway);
    };

    useEffect(() => {
        if (finalPriceNumber && walletBalance < finalPriceNumber) {
            setWalletMessage(t("paymentOptions.walletInsufficientMessage"));
        }
    }, [finalPriceNumber, walletBalance, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await buyPlan({
                accessToken,
                planId,
                mode: selectedMethod,
                ipg: selectedGateway,
                offerCode,
                autoRenewal: autoFieldValues.autoRenewal,
                useWallet: autoFieldValues.useWallet,
            });
            if (res?.ok) {
                setChargeInfo(res?.data);
                if (selectedMethod === "ipg") {
                    window.location.href = res?.data?.ipg?.url;
                } else {
                    toast.success(t("paymentOptions.toasts.walletSuccess"));
                    setTimeout(() => {
                        window.location.href = "/user/plan-list";
                    }, 1500);
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    /* option base classes */
    const optionBase =
        "relative flex items-center py-7 gap-3 pr-4 pl-4 rounded-2xl cursor-pointer " +
        "border transition-all duration-300 backdrop-blur-md " +
        "bg-white/70 dark:!bg-zinc-900/60";

    const optionIdle =
        "border-slate-200/80 dark:!border-zinc-700 " +
        "hover:border-teal-300 dark:hover:!border-teal-700 " +
        "hover:shadow-[0_10px_30px_-15px_rgba(13,148,136,0.4)] " +
        "hover:-translate-y-0.5";

    const optionActive =
        "border-teal-400 dark:!border-teal-500 " +
        "bg-gradient-to-br from-teal-50/80 to-amber-50/60 " +
        "dark:!from-teal-950/40 dark:!to-amber-950/20 " +
        "shadow-[0_15px_40px_-15px_rgba(13,148,136,0.5)] " +
        "ring-2 ring-teal-400/30 dark:ring-teal-500/30";

    const optionDisabled =
        "opacity-50 cursor-not-allowed border-slate-200/60 dark:!border-zinc-800";

    return (
        <>
            <StarField />

            <SidebarProvider>
                <nav className="border-b border-slate-200/70 dark:!border-zinc-700 dark:!bg-zinc-900/90 print:hidden flex items-center fixed top-0 right-0 left-0 bg-white/90 backdrop-blur-xl z-50 shadow-[0_2px_20px_rgba(15,118,110,0.06)]">
                    <Header />
                </nav>

                <div className="vertical-menu rtl:right-0 ltr:left-0 fixed bottom-0 top-16 h-screen border-r border-slate-200/70 bg-white/90 backdrop-blur-xl print:hidden dark:!bg-zinc-900/90 dark:!border-zinc-700 z-40">
                    <UserSidebar />
                </div>
            </SidebarProvider>

            {loading ? (
                <div className="relative z-10 flex justify-center items-center h-screen">
                    <ProgressBar
                        visible={true}
                        height="80"
                        width="80"
                        color="#14b8a6"
                        ariaLabel="progress-bar-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                    />
                </div>
            ) : (
                <div className="relative z-10 flex flex-col min-h-screen mt-4">
                    <div className="main-content flex-grow pt-16 pb-12">
                        <div className="container-fluid px-[0.625rem] mt-10">
                            <div className="grid grid-cols-1 mb-6">
                                <div className="flex items-center justify-between max-w-[500px] mx-auto px-2">
                                    <div>
                                        <div className="mb-2 flex items-center gap-2 text-xs font-bold text-teal-600 dark:!text-teal-400">
                                            <span className="w-2 h-2 rounded-full bg-teal-500 shadow-[0_0_10px_rgba(20,184,166,0.7)] animate-pulse" />
                                            {t("paymentOptions.stepLabel")}
                                        </div>
                                        <h4 className="mb-sm-0 text-xl sm:text-2xl font-bold tracking-tight text-slate-800 dark:!text-white">
                                            {t("paymentOptions.pageTitle")}
                                        </h4>
                                    </div>
                                    <nav className="flex" aria-label="Breadcrumb"></nav>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 px-4 max-w-[500px] w-full mx-auto mt-8">
                            <form onSubmit={handleSubmit}>

                                {/* ==================== AMOUNT BANNER ==================== */}
                                <div className="relative rounded-2xl overflow-hidden mb-5
                                                shadow-[0_15px_40px_-15px_rgba(251,191,36,0.5)]">
                                    <div className="absolute inset-0 bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500" />
                                    <div className="absolute top-0 inset-x-0 h-[2px]
                                                    bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                                    <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full
                                                    bg-white/20 blur-3xl" />
                                    <div className="relative flex items-center justify-between px-5 py-4">
                                        <div className="flex items-center gap-2 text-white">
                                            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm
                                                            ring-1 ring-white/25 flex items-center justify-center">
                                                <Receipt className="w-4 h-4" />
                                            </div>
                                            <span className="text-[13px] font-bold">
                                                {t("paymentOptions.amountLabel")}
                                            </span>
                                        </div>
                                        <div className="flex items-baseline gap-1.5">
                                            <span className={`text-xl font-black text-white tabular-nums ${isEnglish && 'font-sans'}`}>
                                                {finalPrice ? formatNumber(finalPrice) : "0"}
                                            </span>
                                            <span className="text-[11px] font-bold text-white/80">
                                                {t("paymentOptions.currency")}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* ==================== OPTIONS ==================== */}
                                <div className="flex flex-col gap-3">

                                    {/* ---------- Wallet Option ---------- */}
                                    <label
                                        className={`${optionBase} ${isWalletDisabled
                                                ? optionDisabled
                                                : selectedMethod === "wallet"
                                                    ? optionActive
                                                    : optionIdle
                                            }`}
                                        onClick={() => handleSelect("wallet")}
                                    >
                                        <input
                                            type="radio"
                                            value="wallet"
                                            checked={selectedMethod === "wallet"}
                                            readOnly
                                            disabled={isWalletDisabled}
                                            className="peer sr-only"
                                        />

                                        {/* custom radio */}
                                        <span
                                            className={`relative flex-shrink-0 w-5 h-5 rounded-full
                                                        flex items-center justify-center
                                                        transition-all duration-300
                                                        ${selectedMethod === "wallet"
                                                    ? "bg-gradient-to-br from-teal-500 to-teal-700 ring-2 ring-teal-400/40"
                                                    : "bg-white dark:!bg-zinc-800 ring-2 ring-slate-300 dark:!ring-zinc-600"
                                                }`}
                                        >
                                            {selectedMethod === "wallet" && (
                                                <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                            )}
                                        </span>

                                        {/* wallet icon */}
                                        <div className={`w-10 h-10 rounded-xl flex-shrink-0
                                                        flex items-center justify-center
                                                        transition-all duration-300
                                                        ${selectedMethod === "wallet"
                                                ? "bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-500/30"
                                                : "bg-slate-100 dark:!bg-zinc-800 ring-1 ring-slate-200/70 dark:!ring-zinc-700"
                                            }`}>
                                            <Wallet className={`w-4.5 h-4.5 ${selectedMethod === "wallet" ? "text-white" : "text-slate-500 dark:!text-zinc-400"}`} />
                                        </div>

                                        <span className="flex-1 flex flex-col items-start gap-0.5 pr-1">
                                            <span className="text-[13px] font-bold text-slate-800 dark:!text-zinc-100">
                                                {t("paymentOptions.walletOption.title")}
                                            </span>
                                            {isWalletDisabled ? (
                                                <span className="text-[11px] font-semibold text-rose-500 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" />
                                                    {t("paymentOptions.walletOption.insufficient")}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] font-medium text-slate-500 dark:!text-zinc-400">
                                                    {t("paymentOptions.walletOption.balanceLabel")}{" "}
                                                    <span className={`text-teal-600 dark:!text-teal-400 font-bold ${isEnglish && 'font-sans'}`}>
                                                        {formatNumber(walletBalance)}
                                                    </span>{" "}
                                                    {t("paymentOptions.currency")}
                                                </span>
                                            )}
                                        </span>
                                    </label>

                                    {selectedMethod === "wallet" && (
                                        <AutoFields onChange={handleAutoFieldsChange} />
                                    )}

                                    {/* ---------- Gateways ---------- */}
                                    {list && list.length > 0 ? (
                                        list?.map((ipgs, index) => {
                                            const isActive =
                                                selectedMethod === "ipg" && selectedGateway === ipgs;
                                            return (
                                                <label
                                                    key={index}
                                                    className={`${optionBase} ${isGatewayDisabled
                                                            ? optionDisabled
                                                            : isActive
                                                                ? optionActive
                                                                : optionIdle
                                                        }`}
                                                    onClick={() => handleSelect("ipg", ipgs)}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="gateway"
                                                        value={ipgs}
                                                        checked={isActive}
                                                        disabled={isGatewayDisabled}
                                                        readOnly
                                                        className="peer sr-only"
                                                    />

                                                    {/* custom radio */}
                                                    <span
                                                        className={`relative flex-shrink-0 w-5 h-5 rounded-full
                                                                    flex items-center justify-center
                                                                    transition-all duration-300
                                                                    ${isActive
                                                                ? "bg-gradient-to-br from-teal-500 to-teal-700 ring-2 ring-teal-400/40"
                                                                : "bg-white dark:!bg-zinc-800 ring-2 ring-slate-300 dark:!ring-zinc-600"
                                                            }`}
                                                    >
                                                        {isActive && (
                                                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                                        )}
                                                    </span>

                                                    {/* gateway icon */}
                                                    <div className={`w-10 h-10 rounded-xl flex-shrink-0
                                                                    flex items-center justify-center
                                                                    transition-all duration-300
                                                                    ${isActive
                                                            ? "bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-500/30"
                                                            : "bg-slate-100 dark:!bg-zinc-800 ring-1 ring-slate-200/70 dark:!ring-zinc-700"
                                                        }`}>
                                                        <CreditCard className={`w-4.5 h-4.5 ${isActive ? "text-white" : "text-slate-500 dark:!text-zinc-400"}`} />
                                                    </div>

                                                    <span className="flex-1 flex items-center justify-between gap-2 pr-1">
                                                        <span className="text-[13px] font-bold text-slate-800 dark:!text-zinc-100">
                                                            {t("paymentOptions.gatewayOption.title")}
                                                        </span>
                                                        <img
                                                            style={{ width: "60px", height: "40px", objectFit: "contain" }}
                                                            src={`${ipgs === "zarinpal" ? "./Zarrinpal-Logo.png" : "./zibal.png"}`}
                                                            alt={ipgs}
                                                        />
                                                    </span>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <div className="flex items-center gap-3 px-5 py-5 rounded-2xl
                                                        bg-rose-50/70 dark:!bg-rose-950/20
                                                        border border-rose-200 dark:!border-rose-900/50
                                                        backdrop-blur-sm">
                                            <div className="w-10 h-10 rounded-xl flex-shrink-0
                                                            bg-gradient-to-br from-rose-500 to-red-600
                                                            flex items-center justify-center
                                                            shadow-lg shadow-rose-500/30">
                                                <AlertCircle className="w-5 h-5 text-white" />
                                            </div>
                                            <span className="text-[13px] font-bold text-rose-700 dark:!text-rose-300">
                                                {t("paymentOptions.noGateway")}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* ==================== RULES CHECKBOX ==================== */}
                                <label
                                    className={`flex items-start gap-3 mt-5 py-4 px-4 rounded-2xl cursor-pointer
                                                backdrop-blur-sm transition-all duration-300
                                                ${rules
                                            ? "bg-gradient-to-br from-teal-50/80 to-amber-50/60 dark:!from-teal-950/40 dark:!to-amber-950/20 ring-1 ring-teal-300/60 dark:!ring-teal-500/30"
                                            : "bg-white/60 dark:!bg-zinc-900/50 ring-1 ring-slate-200/70 dark:!ring-zinc-700 hover:ring-teal-300/60 dark:hover:!ring-teal-700"
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        value="rules"
                                        checked={rules}
                                        onChange={() => setRules(!rules)}
                                        className="peer sr-only"
                                    />

                                    {/* custom checkbox */}
                                    <span
                                        className={`relative mt-0.5 w-5 h-5 rounded-lg flex-shrink-0
                                                    flex items-center justify-center
                                                    transition-all duration-200
                                                    ${rules
                                                ? "bg-gradient-to-br from-teal-600 to-teal-800 ring-2 ring-teal-500/40"
                                                : "bg-white dark:!bg-zinc-800 ring-2 ring-slate-300 dark:!ring-zinc-600"
                                            }`}
                                    >
                                        {rules && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                                    </span>

                                    <span className="text-[13px] leading-6 text-slate-600 dark:!text-zinc-300">
                                        {t("paymentOptions.rules.prefix")}{" "}
                                        <span className="font-bold text-teal-700 dark:!text-teal-300">
                                            {t("paymentOptions.rules.link")}
                                        </span>{" "}
                                        {t("paymentOptions.rules.suffix")}
                                    </span>
                                </label>

                                {/* ==================== SUBMIT BUTTON ==================== */}
                                <button
                                    type="submit"
                                    disabled={
                                        !selectedMethod ||
                                        (selectedMethod === "ipg" && (!selectedGateway || isGatewayDisabled)) ||
                                        !rules
                                    }
                                    className={`group relative mt-5 mb-5 w-full rounded-2xl px-4 py-3.5
                                                overflow-hidden text-[14px] font-black
                                                transition-all duration-500
                                                ${!selectedMethod || !rules
                                            ? "bg-slate-200 dark:!bg-zinc-800 text-slate-400 dark:!text-zinc-500 cursor-not-allowed"
                                            : `bg-gradient-to-r from-teal-700 via-teal-600 to-amber-600
                                                       bg-[length:200%_100%] hover:bg-[position:100%_0]
                                                       text-white
                                                       shadow-[0_15px_40px_-12px_rgba(13,148,136,0.6)]
                                                       hover:shadow-[0_20px_50px_-12px_rgba(251,191,36,0.6)]
                                                       hover:scale-[1.01] active:scale-[0.99]`
                                        }`}
                                >
                                    {selectedMethod && rules && (
                                        <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full
                                                         transition-transform duration-1000
                                                         bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                                    )}
                                    <span className="relative z-10 flex items-center justify-center gap-2">
                                        <ShieldCheck className="w-4 h-4" />
                                        {t("paymentOptions.submit")}
                                    </span>
                                </button>

                                {/* secure hint */}
                                <p className="flex items-center justify-center gap-1.5 text-[11px]
                                              text-slate-400 dark:!text-zinc-500">
                                    <ShieldCheck className="w-3 h-3 text-emerald-500" />
                                    {t("paymentOptions.secureHint")}
                                </p>
                            </form>
                        </div>

                        <Footer />
                    </div>
                </div>
            )}
        </>
    );
};

export default PaymentOptions;